import React, { useEffect, useState } from 'react';
import { EventShortInfo } from '../../ReportsClasses';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Header, Icon, Label, Message, Table } from 'react-frontier';
import { addCommas, bindClose } from '@arema/components/Util';
import { firstBy } from 'thenby';
import { Modal } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import API from '../../ReportsAPI';

interface EventStatsPayoutProps{
	event_id: number,
	event: EventShortInfo
}

interface EventPayout{
	payout_id: number,
	completed: boolean,
	account_id: number,
	amount: number,
	clabe: string,
	card_number: string,
	date_scheduled: number,
	date_created: number,
	date_completed: number,
}

interface PayoutDetails{
	payout_id: number,
	completed: boolean,
	cancelled: boolean,
	rejected: boolean,
	reject_feedback: string,
	date_created: number,
	date_scheduled: number,
	date_completed: number,
	date_cancelled: number,
	amount: number,
	account_id: number,
	name_account: string,
	bank: string,
	clabe: string,
	card_number: string,
	evidence: {
		filename: string,
		date_created: number,
		url: string,
	}[]
}

var EventStatsPayout = (props: EventStatsPayoutProps)=>{
	var { t } = useTranslation();
	var [payouts, setPayouts] = useState<EventPayout[]>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [viewingPayout, setViewingPayout] = useState<number>(null);
	var [payoutDetails, setPayoutDetails] = useState<PayoutDetails>(null);
	var [modalLoadError, setModalLoadError] = useState<string>(null);

	useEffect(()=>{
		API.getEventPayouts(props.event_id).then(res=>{
			if(res.error) return setLoadError(res.message);
			setPayouts(res.data);
		}).catch(err=>{
			return setLoadError(t('common.unexpected_code', { code: 'ST-LCL-EVSTSPY-1' }))
		})
	}, [props.event_id]);

	var viewPayout = (payout_id: number)=>{
		return ()=>{
			setViewingPayout(payout_id);
			setModalLoadError(null);
			if(payoutDetails?.payout_id!==payout_id){
				setPayoutDetails(null);
				API.getPayout(props.event_id, payout_id).then(res=>{
					if(res.error) return setModalLoadError(res.message);
					return setPayoutDetails(res.data);
				}).catch(err=>{
					return setModalLoadError(t('common.unexpected_code', { code: 'ST-LCL-EVSTSPY-2' }))
				})
			}
		}
	}

	if(loadError) return <Header text={t('common.error')} subtext={loadError} iconName='face-frown-open' />
	if(!payouts) return <Header loading text={t('common.loading')} />
	
	return <div className="ar event-stats-payouts-screen">
		<Header text={props.event.event_name} subtext={t('payouts.title')} />
		<Message type='info' style={{ maxWidth: 600, margin: 'auto', marginBottom: 15, textAlign: 'center' }}>
			<Trans i18nKey={"payouts.message"} />
		</Message>
		<Table
			striped
			centeredIndexes={[0]}
			title={t('payouts.title')}
			style={{ margin: 'auto', maxWidth: 700 }}
			headers={[
				'ID',
				t('payouts.table.date'),
				t('payouts.table.account'),
				t('payouts.table.status'),
				t('payouts.table.amount')
			]}
		>
			{payouts.sort(firstBy('completed', 'asc').thenBy('date_completed', 'desc')).map(a=>(
				<Table.Row
					selectable
					key={`PYT-${a.payout_id}`}
					collapsingIndexes={[0,1,3,4]}
					centeredIndexes={[0]}
					onClick={viewPayout(a.payout_id)}
					data={[
						a.payout_id,
						moment.unix(a.date_completed || a.date_scheduled || a.date_created).format('DD/MMM/YY HH:mm'),
						<>
							<Icon name={a.clabe ? 'building' : (a.card_number ? 'credit-card' : 'money-bill-wave')} />
							{a.clabe ? `SPEI **${a.clabe}` : (a.card_number ? a.card_number : t('payouts.other'))}
						</>,
						<Label color={a.completed ? 'green' : 'blue'} value={a.completed ? t('payouts.completed') : t('payouts.pending')} style={{ minWidth: 108 }} />,
						addCommas(a.amount)
					]}
				/>
			))}
		</Table>
		<Modal open={!!viewingPayout} onClose={bindClose(setViewingPayout)} size='tiny'>
			<Modal.Header>{t('payouts.details.title')} {viewingPayout}</Modal.Header>
			<Modal.Content>
				{modalLoadError ? (
					<Header text={t('common.error')} subtext={modalLoadError} iconName='face-frown-open' style={{ marginTop: 50, marginBottom: 50 }} />
				) : payoutDetails ? (
					<Table details striped title='Información'>
						<Table.Row data={['ID', payoutDetails.payout_id]} />
						<Table.Row data={[t('payouts.table.status'), (
							<Label color={payoutDetails.completed ? 'green' : 'blue'} value={payoutDetails.completed ? t('payouts.completed') : t('payouts.pending')} />
						)]} />
						<Table.Row data={[t('payouts.details.date_created'), moment.unix(payoutDetails.date_created).format('DD/MM/YYYY HH:mm')]} />
						{(!payoutDetails.completed && !!payoutDetails.date_scheduled) && (
							<Table.Row data={[t('payouts.details.date_scheduled'), moment.unix(payoutDetails.date_scheduled).format('DD/MM/YYYY HH:mm')]} />
						)}
						{!!payoutDetails.completed && (
							<Table.Row data={[t('payouts.details.date_completed'), moment.unix(payoutDetails.date_completed).format('DD/MM/YYYY HH:mm')]} />
						)}
						{(!payoutDetails.cancelled && !!payoutDetails.date_cancelled) && (
							<Table.Row data={[t('payouts.details.date_cancelled'), moment.unix(payoutDetails.date_cancelled).format('DD/MM/YYYY HH:mm')]} />
						)}
						<Table.Row data={[t('payouts.table.amount'), `$${addCommas(payoutDetails.amount)}`]} />
						{!!payoutDetails.account_id && <>
							<Table.Row header>
								<Table.Cell colSpan={2}>Cuenta deposito</Table.Cell>
							</Table.Row>
							<Table.Row data={[t('payouts.details.account_name'), payoutDetails.name_account]} />
							<Table.Row data={[t('payouts.details.bank'), payoutDetails.bank]} />
							<Table.Row data={[t('payouts.details.bank_account'), <>
								<Icon name={payoutDetails.clabe ? 'building' : (payoutDetails.card_number ? 'credit-card' : 'money-bill-wave')} />
								{payoutDetails.clabe ? `SPEI **${payoutDetails.clabe}` : (payoutDetails.card_number ? payoutDetails.card_number : t('payouts.other'))}
							</>]} />
						</>}
						<Table.Row header>
							<Table.Cell colSpan={2}>Archivos adjuntos</Table.Cell>
						</Table.Row>
						{payoutDetails.evidence.length>0 ? (
							payoutDetails.evidence.map(a=>(
								<Table.Cell row colSpan={2}>
									<div style={{ fontWeight: 'normal', display: 'flex', justifyContent: 'space-between', width: '100%', flexDirection: 'row' }}>
										<div className="text">{a.filename}</div>
										<Button icon as={Link} to={a.url} target='_blank' size='tiny' iconName='file-download' />
									</div>
								</Table.Cell>
							))
						) : (
							<Table.Cell empty row centered colSpan={2} style={{ fontWeight: 'normal' }}>No hay archivos adjuntos</Table.Cell>
						)}
					</Table>
				) : (
					<Header loading text={t('common.loading')} style={{ marginTop: 100, marginBottom: 100 }} />
				)}
			</Modal.Content>
			<Modal.Actions>
				<Button text={t('common.close')} onClick={bindClose(setViewingPayout)} />
			</Modal.Actions>
		</Modal>
	</div>
}

export default EventStatsPayout;